import { FiveADayIcon, PlantDiversityIcon, WholeFoodScoreIcon } from "@mealmanager/icons";
import { DownOutlined } from "@ant-design/icons";
import { Grid, Spin, Typography, theme } from "antd";
import { type CSSProperties, type ReactNode, useMemo } from "react";

const { Text } = Typography;

export const MACRO_TAG_CONFIG = {
  calories: { label: "Calories", shortLabel: "Kcal", unit: "kcal" },
  carbs: { label: "Carbs", shortLabel: "Carb", unit: "g" },
  netCarbs: { label: "Net Carbs", shortLabel: "Net", unit: "g" },
  proteins: { label: "Proteins", shortLabel: "Prot", unit: "g" },
  fat: { label: "Fats", shortLabel: "Fat", unit: "g" },
  fibers: { label: "Fibers", shortLabel: "Fib", unit: "g" },
} as const;

type MacroKey = keyof typeof MACRO_TAG_CONFIG;
export type MacroTagKey = MacroKey;

export type NutritionContext = "recipe" | "meal-plan";

export type NutritionMacros = {
  calories?: number | null;
  carbs?: number | null;
  netCarbs?: number | null;
  fat?: number | null;
  fibers?: number | null;
  proteins?: number | null;
  plantDiversity?: number | null;
  fiveADayPortions?: number | null;
  realOVoreScore?: number | null;
  enrichmentStartTime?: number | null;
};

export type MacrosTableProps = {
  macros: NutritionMacros;
  small?: boolean;
  topLeftContent?: ReactNode;
  context?: NutritionContext;
  condensed?: boolean;
  style?: CSSProperties;
};

type NutritionPalette = {
  cardBgMuted: string;
  surfaceAccent: string;
  text: string;
  textMuted: string;
  border: string;
  borderStrong: string;
  accent: string;
  accentStrong: string;
  amber: string;
  amberSoft: string;
  orange: string;
  orangeSoft: string;
};

export function MacrosTable({
  macros,
  small,
  topLeftContent,
  context = "recipe",
  condensed,
  style,
}: MacrosTableProps) {
  const recalculating = !!macros?.enrichmentStartTime;
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);
  const macroKeys: MacroKey[] = ["calories", "carbs", "netCarbs", "proteins", "fat", "fibers"];
  const condensedMacroKeys: MacroKey[] = ["calories", "netCarbs", "proteins", "fat", "fibers"];
  const hasPlantScores =
    (macros?.plantDiversity !== null && macros?.plantDiversity !== undefined) ||
    (macros?.fiveADayPortions !== null && macros?.fiveADayPortions !== undefined);
  const showRealOVoreScore = context === "recipe" || context === "meal-plan";
  const condensedRealOVoreScoreNumeric = parseMacroNumber(macros?.realOVoreScore);
  const condensedRealOVoreScoreColor = realOVoreScoreColor(
    condensedRealOVoreScoreNumeric === null ? null : Math.max(0, Math.min(100, condensedRealOVoreScoreNumeric)),
    token
  );
  const splitData = useMemo(
    () => buildMacroSplit(nutritionPalette, macros?.netCarbs, macros?.proteins, macros?.fat),
    [macros?.netCarbs, macros?.proteins, macros?.fat, nutritionPalette]
  );

  if (condensed) {
    return (
      <div style={{ display: "grid", gap: 16, width: "100%", ...style }}>
        {topLeftContent ? <div>{topLeftContent}</div> : null}

        {hasPlantScores || showRealOVoreScore ? (
          <div
            aria-label="Plants"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: small ? "center" : "space-between",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            {hasPlantScores ? (
              <>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <PlantDiversityIcon size={18} color={nutritionPalette.accent} data-testid="plant-diversity-icon" />
                  <Text style={{ color: nutritionPalette.text, fontSize: 13, lineHeight: 1.2 }}>
                    <Text strong style={{ color: "inherit", fontSize: 13 }}>
                      {formatScoreValue(macros?.plantDiversity)}
                    </Text>{" "}
                    plants
                  </Text>
                </span>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <FiveADayIcon size={18} color={nutritionPalette.accent} data-testid="five-a-day-icon" />
                  <Text style={{ color: nutritionPalette.text, fontSize: 13, lineHeight: 1.2 }}>
                    <Text strong style={{ color: "inherit", fontSize: 13 }}>
                      {formatScoreValue(macros?.fiveADayPortions)}
                    </Text>{" "}
                    portions/day
                  </Text>
                </span>
              </>
            ) : null}
            {showRealOVoreScore ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <WholeFoodScoreIcon size={18} color={nutritionPalette.accent} data-testid="real-o-meter-icon" />
                <Text style={{ color: nutritionPalette.text, fontSize: 13, lineHeight: 1.2 }}>
                  <Text strong style={{ color: condensedRealOVoreScoreColor ?? "inherit", fontSize: 13 }}>
                    {condensedRealOVoreScoreText(macros?.realOVoreScore)}
                  </Text>{" "}
                  {small ? "whole" : "whole food score"}
                </Text>
              </span>
            ) : null}
          </div>
        ) : null}

        {recalculating ? (
          <div
            style={{
              minHeight: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 10,
              border: `1px solid ${nutritionPalette.border}`,
              background: nutritionPalette.cardBgMuted,
              color: token.colorTextSecondary,
            }}
          >
            <Text type="secondary">
              <Spin size="small" style={{ marginRight: 8 }} />
              Recalculating nutrition...
            </Text>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
              gap: 4,
              width: "100%",
            }}
          >
            {condensedMacroKeys.map((key) => (
              <MacroValueChip key={key} macroKey={key} value={macros?.[key]} small />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16, width: "100%", ...style }}>
      {topLeftContent ? <div>{topLeftContent}</div> : null}

      {hasPlantScores || showRealOVoreScore ? (
        <section aria-label="Plants" style={{ marginBottom: 6 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 0,
              alignItems: "start",
            }}
          >
            {hasPlantScores ? (
              <>
                <PlantDiversityScore
                  label="Plant Diversity"
                  value={macros?.plantDiversity}
                  target={30}
                  context={context}
                />
                <FiveADayScore
                  label="5 A Day"
                  value={macros?.fiveADayPortions}
                  context={context}
                />
              </>
            ) : null}
            {showRealOVoreScore ? (
              <RealOVoreScore value={macros?.realOVoreScore} withDivider={hasPlantScores} context={context} />
            ) : null}
          </div>
        </section>
      ) : null}

      <section aria-label="Macro Nutrients" style={{ display: "grid", gap: small ? 10 : 12 }}>
        <SectionHeading>Macro Nutrients</SectionHeading>
        {recalculating ? (
          <div
            style={{
              minHeight: 52,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 10,
              border: `1px solid ${nutritionPalette.border}`,
              background: nutritionPalette.cardBgMuted,
              color: token.colorTextSecondary,
            }}
          >
            <Text type="secondary">
              <Spin size="small" style={{ marginRight: 8 }} />
              Recalculating nutrition...
            </Text>
          </div>
        ) : (
          <MacroSplitRing segments={splitData} small={small} />
        )}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            columnGap: small ? 6 : 8,
            rowGap: small ? 10 : 12,
          }}
        >
          {macroKeys.map((key) => (
            <MacroValueChip key={key} macroKey={key} value={macros?.[key]} small={small} />
          ))}
        </div>
      </section>
    </div>
  );
}

export type IngredientMacrosProps = NutritionMacros & {
  height?: number;
  rounded?: boolean;
};

export function IngredientMacros({
  calories,
  carbs,
  netCarbs,
  fat,
  fibers,
  proteins,
  height = 20,
  rounded = true,
}: IngredientMacrosProps) {
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);
  const splitData = useMemo(
    () => buildMacroSplit(nutritionPalette, netCarbs, proteins, fat),
    [fat, netCarbs, nutritionPalette, proteins]
  );
  const screens = Grid.useBreakpoint();
  const isMd = !!screens.md;

  return (
    <div
      data-testid="ingredient-macros-layout"
      style={{
        display: "grid",
        gridTemplateColumns: isMd ? "minmax(108px, 0.75fr) minmax(0, 2fr)" : "minmax(0, 1fr)",
        gap: isMd ? 6 : 8,
        alignItems: "center",
        width: "100%",
      }}
    >
      <MacroSplitBar
        segments={splitData}
        compact
        height={height}
        rounded={rounded}
        ariaLabel="Ingredient macros split"
      />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
          gap: 4,
          width: "100%",
          marginBottom: isMd ? 2 : 0,
        }}
      >
        <MacroValueChip macroKey="calories" value={calories} small />
        <MacroValueChip macroKey="carbs" value={carbs} small />
        <MacroValueChip macroKey="netCarbs" value={netCarbs} small />
        <MacroValueChip macroKey="proteins" value={proteins} small />
        <MacroValueChip macroKey="fat" value={fat} small />
        <MacroValueChip macroKey="fibers" value={fibers} small />
      </div>
    </div>
  );
}

export function RecipeMacroHighlights({
  calories,
  netCarbs,
  proteins,
  fat,
  fibers,
}: Pick<NutritionMacros, "calories" | "netCarbs" | "proteins" | "fat" | "fibers">) {
  return (
    <div
      aria-label="Recipe macro highlights"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
        gap: 4,
        width: "100%",
      }}
    >
      <MacroValueChip macroKey="calories" value={calories} small />
      <MacroValueChip macroKey="netCarbs" value={netCarbs} small />
      <MacroValueChip macroKey="proteins" value={proteins} small />
      <MacroValueChip macroKey="fat" value={fat} small />
      <MacroValueChip macroKey="fibers" value={fibers} small />
    </div>
  );
}

export type CollapsibleRecipeMacrosProps = Pick<
  NutritionMacros,
  "calories" | "netCarbs" | "proteins" | "fat" | "fibers"
> & {
  hideByDefault: boolean;
  open: boolean;
  onToggle: () => void;
  testId?: string;
  token: ReturnType<typeof theme.useToken>["token"];
};

/**
 * Splits a recipe card's macro strip into a toggle button (to place inline with the
 * tags row) and a panel (the pills themselves). `open`/`onToggle` are owned by the
 * caller (not internal state) because this is invoked per-item inside list renders,
 * where a local useState would violate the rules of hooks. `token` is passed in
 * (rather than read via theme.useToken() here) for the same reason — this isn't a
 * component, so it can't call hooks itself. Colors come from the antd theme token
 * rather than the authed app's --app-* CSS variables, since those are only ever set
 * by authed's AppShell and are undefined in the public app.
 */
export function getCollapsibleRecipeMacros({
  hideByDefault,
  open,
  onToggle,
  testId,
  calories,
  netCarbs,
  proteins,
  fat,
  fibers,
  token,
}: CollapsibleRecipeMacrosProps): { button: ReactNode; panel: ReactNode } {
  if (!hideByDefault) {
    return {
      button: null,
      panel: (
        <div data-testid={testId}>
          <RecipeMacroHighlights calories={calories} netCarbs={netCarbs} proteins={proteins} fat={fat} fibers={fibers} />
        </div>
      ),
    };
  }

  const nutritionPalette = getNutritionPalette(token);

  return {
    button: (
      <button
        type="button"
        onClick={onToggle}
        aria-label={open ? "Hide Macros" : "Show Macros"}
        aria-expanded={open}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          padding: "3px 10px",
          borderRadius: 999,
          border: `1px solid ${open ? nutritionPalette.accent : nutritionPalette.border}`,
          background: open ? nutritionPalette.accent : token.colorBgContainer,
          color: open ? "#fff" : nutritionPalette.textMuted,
          fontSize: 11,
          lineHeight: 1,
          cursor: "pointer",
        }}
      >
        Macros
        <DownOutlined
          style={{
            fontSize: 11,
            color: open ? "#fff" : nutritionPalette.textMuted,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s ease",
          }}
        />
      </button>
    ),
    panel: open ? (
      <div data-testid={testId}>
        <RecipeMacroHighlights calories={calories} netCarbs={netCarbs} proteins={proteins} fat={fat} fibers={fibers} />
      </div>
    ) : null,
  };
}

function SectionHeading({ children }: { children: string }) {
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);

  return (
    <Text
      strong
      style={{
        color: nutritionPalette.accentStrong,
        fontSize: 12,
        letterSpacing: 0,
        textTransform: "uppercase",
      }}
    >
      {children}
    </Text>
  );
}

function PlantDiversityScore({
  label,
  value,
  target,
  context,
}: {
  label: string;
  value?: number | null;
  target: number;
  context: NutritionContext;
}) {
  const numeric = parseMacroNumber(value);
  const score = numeric === null ? null : Math.max(0, numeric);
  const progress = score === null ? 0 : Math.min(1, score / target);

  return (
    <ScorePanel>
      <ScoreLabel>{label}</ScoreLabel>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 5, minHeight: 38 }}>
        <IconBadge>
          <PlantDiversityIcon size={22} data-testid="plant-diversity-icon" />
        </IconBadge>
        <div style={{ minWidth: 0 }}>
          <ScoreValue value={formatScoreValue(score)} unit="plants" stacked />
        </div>
      </div>
      {context === "meal-plan" ? (
        <ProgressBar progress={progress} label={`${label} progress`} />
      ) : null}
    </ScorePanel>
  );
}

function FiveADayScore({
  label,
  value,
  context,
}: {
  label: string;
  value?: number | null;
  context: NutritionContext;
}) {
  const numeric = parseMacroNumber(value);
  const score = numeric === null ? null : Math.max(0, numeric);
  const progress = score === null ? 0 : Math.min(1, score / 5);

  return (
    <ScorePanel withDivider>
      <ScoreLabel>{label}</ScoreLabel>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 5, minHeight: 38 }}>
        <IconBadge>
          <FiveADayIcon size={22} data-testid="five-a-day-icon" />
        </IconBadge>
        <div style={{ minWidth: 0 }}>
          <ScoreValue value={formatScoreValue(score)} unit="portions" stacked />
        </div>
      </div>
      {context === "meal-plan" ? (
        <ProgressBar progress={progress} label={`${label} progress`} />
      ) : null}
    </ScorePanel>
  );
}

function condensedRealOVoreScoreText(value?: number | null): string {
  const numeric = parseMacroNumber(value);
  if (numeric === null) return "—";
  return `${formatScoreValue(Math.max(0, Math.min(100, numeric)))}%`;
}

function realOVoreScoreColor(score: number | null, token: ReturnType<typeof theme.useToken>["token"]): string | undefined {
  if (score === null) return undefined;
  if (score < 50) return token.colorError;
  if (score > 80) return token.colorPrimary;
  return undefined;
}

function RealOVoreScore({
  value,
  withDivider,
  context,
}: {
  value?: number | null;
  withDivider: boolean;
  context: NutritionContext;
}) {
  const { token } = theme.useToken();
  const numeric = parseMacroNumber(value);
  const score = numeric === null ? null : Math.max(0, Math.min(100, numeric));
  const valueColor = realOVoreScoreColor(score, token);
  const progress = score === null ? 0 : score / 100;

  return (
    <ScorePanel withDivider={withDivider}>
      <ScoreLabel>Whole Food Score</ScoreLabel>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 5, minHeight: 38 }}>
        <IconBadge>
          <WholeFoodScoreIcon size={22} data-testid="real-o-meter-icon" />
        </IconBadge>
        <div style={{ minWidth: 0 }}>
          <ScoreValue value={score === null ? "—" : `${formatScoreValue(score)}%`} unit="" valueColor={valueColor} />
        </div>
      </div>
      {context === "meal-plan" ? (
        <ProgressBar progress={progress} label="Whole Food Score progress" />
      ) : null}
    </ScorePanel>
  );
}

function ScorePanel({ children, withDivider }: { children: ReactNode; withDivider?: boolean }) {
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);

  return (
    <div
      style={{
        minWidth: 0,
        padding: withDivider ? "0 0 0 3px" : 0,
        borderLeft: withDivider ? `1px solid ${nutritionPalette.border}` : undefined,
        display: "grid",
        gap: 4,
        justifyItems: "center",
        textAlign: "center",
      }}
    >
      {children}
    </div>
  );
}

function ScoreValue({
  value,
  unit,
  stacked,
  valueColor,
}: {
  value: string;
  unit: string;
  stacked?: boolean;
  valueColor?: string;
}) {
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: stacked ? "column" : "row",
        alignItems: "center",
        gap: stacked ? 2 : 6,
        minWidth: 0,
        whiteSpace: "nowrap",
      }}
    >
      <Text
        strong
        style={{
          color: valueColor ?? nutritionPalette.text,
          fontSize: 22,
          lineHeight: 1,
          letterSpacing: 0,
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Text>
      <Text
        style={{
          color: nutritionPalette.text,
          fontSize: 10,
          lineHeight: 1.1,
          maxWidth: stacked ? "none" : 38,
          whiteSpace: "nowrap",
        }}
      >
        {unit}
      </Text>
    </div>
  );
}

function ScoreLabel({ children }: { children: string }) {
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);

  return (
    <Text
      strong
      style={{
        color: nutritionPalette.accentStrong,
        fontSize: 9,
        letterSpacing: 0,
        lineHeight: 1.1,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </Text>
  );
}

function IconBadge({ children }: { children: ReactNode }) {
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);

  return (
    <div
      aria-hidden="true"
      style={{
        width: 32,
        height: 32,
        borderRadius: 999,
        background: nutritionPalette.surfaceAccent,
        color: nutritionPalette.accentStrong,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {children}
    </div>
  );
}

function ProgressBar({ progress, label }: { progress: number; label: string }) {
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);

  return (
    <div
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      style={{
        width: "100%",
        height: 8,
        borderRadius: 999,
        overflow: "hidden",
        background: nutritionPalette.border,
        border: "none",
      }}
    >
      <div
        style={{
          width: `${progress * 100}%`,
          height: "100%",
          borderRadius: 999,
          background: nutritionPalette.accent,
          transition: "width 0.4s ease",
        }}
      />
    </div>
  );
}

function MacroValueChip({ macroKey, value, small }: { macroKey: MacroKey; value?: number | null; small?: boolean }) {
  const config = MACRO_TAG_CONFIG[macroKey];
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);

  return (
    <div
      title={config.label}
      style={getMacroChipStyle(nutritionPalette, small)}
    >
      <Text
        strong
        style={{
          color: nutritionPalette.text,
          fontSize: small ? 13 : 15,
          lineHeight: 1.2,
          paddingBottom: 2,
          whiteSpace: "nowrap",
          overflow: "hidden",
          maxWidth: "100%",
        }}
      >
        {formatMacroDisplayValue(macroKey, value)}
      </Text>
      <Text
        style={{
          color: nutritionPalette.textMuted,
          fontSize: small ? 10 : 11,
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          overflow: "hidden",
          maxWidth: "100%",
        }}
      >
        {config.label}
      </Text>
    </div>
  );
}

function getMacroChipStyle(nutritionPalette: NutritionPalette, small?: boolean): CSSProperties {
  return {
    minWidth: 0,
    minHeight: small ? 40 : 58,
    padding: 0,
    borderRadius: 10,
    border: `1px solid ${nutritionPalette.border}`,
    background: nutritionPalette.cardBgMuted,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 0,
    textAlign: "center",
    overflow: "hidden",
  };
}

export function getFilledMacroTagStyle(_key: MacroTagKey): CSSProperties {
  return getMacroChipStyle(getStaticNutritionPalette(), true);
}

type MacroSplitSegment = {
  key: string;
  label: string;
  value: number;
  color: string;
  border: string;
};

function MacroSplitBar({
  segments,
  compact,
  height,
  rounded,
  ariaLabel = "Recipe macro split",
}: {
  segments: MacroSplitSegment[];
  compact?: boolean;
  height?: number;
  rounded?: boolean;
  ariaLabel?: string;
}) {
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  if (total <= 0) {
    return (
      <div
        role="img"
        aria-label={ariaLabel}
        style={getMacroSplitFrameStyle(nutritionPalette, { compact, height, rounded })}
      >
        <Text type="secondary" style={{ fontSize: compact ? 11 : 12 }}>
          No macro split available
        </Text>
      </div>
    );
  }

  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={getMacroSplitFrameStyle(nutritionPalette, { compact, height, rounded })}
    >
      {segments.map((segment) => {
        const percent = segment.value / total;
        return (
          <div
            key={segment.key}
            title={`${segment.label}: ${Math.round(segment.value)}g, ${Math.round(percent * 100)}%`}
            style={{
              width: `${percent * 100}%`,
              minWidth: 0,
              minHeight: compact ? height : 52,
              padding: compact ? "0 2px" : "6px 4px",
              display: "flex",
              flexDirection: compact ? "row" : "column",
              alignItems: "center",
              justifyContent: "center",
              gap: compact ? 0 : 2,
              background: segment.color,
              boxShadow: `inset 0 0 0 1px ${segment.border}`,
              color: nutritionPalette.accentStrong,
              textAlign: "center",
              overflow: "hidden",
            }}
          >
            {!compact ? (
              <>
                <Text strong style={{ color: "inherit", fontSize: percent < 0.18 ? 10 : 12, lineHeight: 1 }}>
                  {Math.round(percent * 100)}%
                </Text>
                <Text style={{ color: "inherit", fontSize: percent < 0.18 ? 9 : 10, lineHeight: 1.1 }}>
                  {segment.label}
                </Text>
              </>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function MacroSplitRing({
  segments,
  small,
}: {
  segments: MacroSplitSegment[];
  small?: boolean;
}) {
  const { token } = theme.useToken();
  const nutritionPalette = getNutritionPalette(token);
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  if (total <= 0) {
    return (
      <div
        role="img"
        aria-label="Recipe macro split"
        style={{
          minHeight: small ? 92 : 112,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 10,
          border: `1px solid ${nutritionPalette.border}`,
          background: nutritionPalette.cardBgMuted,
        }}
      >
        <Text type="secondary" style={{ fontSize: small ? 11 : 12 }}>
          No macro split available
        </Text>
      </div>
    );
  }

  let cumulativeAngle = -90;
  const ringSegments = segments
    .filter((segment) => segment.value > 0)
    .map((segment) => {
      const startAngle = cumulativeAngle;
      cumulativeAngle += (segment.value / total) * 360;
      return { ...segment, startAngle, endAngle: cumulativeAngle };
    });
  const size = small ? 92 : 112;

  return (
    <div
      role="img"
      aria-label="Recipe macro split"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: small ? 14 : 18,
        minHeight: size,
      }}
    >
      <div
        aria-hidden="true"
        style={{
          width: size,
          height: size,
          flex: `0 0 ${size}px`,
          borderRadius: "50%",
          position: "relative",
        }}
      >
        <svg viewBox="0 0 100 100" width="100%" height="100%" aria-hidden="true">
          {ringSegments.map((segment) => (
            <path
              key={segment.key}
              d={getDonutSegmentPath(segment.startAngle, segment.endAngle)}
              fill={segment.color}
              stroke={segment.border}
              strokeWidth="1"
            />
          ))}
        </svg>
        <div
          style={{
            position: "absolute",
            inset: small ? 22 : 26,
            borderRadius: "50%",
            background: nutritionPalette.cardBgMuted,
          }}
        />
      </div>
      <div style={{ display: "grid", gap: small ? 6 : 8, minWidth: 0 }}>
        {segments.map((segment) => {
          const percent = Math.round((segment.value / total) * 100);
          return (
            <div key={segment.key} style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: "50%",
                  flex: "0 0 auto",
                  background: segment.color,
                  border: `1px solid ${segment.border}`,
                }}
              />
              <Text style={{ color: nutritionPalette.text, fontSize: small ? 12 : 13, minWidth: 0 }}>
                {segment.label} ({percent}%)
              </Text>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function getDonutSegmentPath(startAngle: number, endAngle: number): string {
  const outerRadius = 49;
  const innerRadius = 27;
  const angleSpan = endAngle - startAngle;

  if (angleSpan >= 359.99) {
    return `M 50 1 A 49 49 0 1 1 49.99 1 Z M 50 23 A 27 27 0 1 0 50.01 23 Z`;
  }

  const outerStart = pointOnCircle(outerRadius, startAngle);
  const outerEnd = pointOnCircle(outerRadius, endAngle);
  const innerEnd = pointOnCircle(innerRadius, endAngle);
  const innerStart = pointOnCircle(innerRadius, startAngle);
  const largeArc = angleSpan > 180 ? 1 : 0;

  return [
    `M ${outerStart.x} ${outerStart.y}`,
    `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerEnd.x} ${outerEnd.y}`,
    `L ${innerEnd.x} ${innerEnd.y}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerStart.x} ${innerStart.y}`,
    "Z",
  ].join(" ");
}

function pointOnCircle(radius: number, angle: number): { x: number; y: number } {
  const radians = (angle * Math.PI) / 180;
  return {
    x: 50 + radius * Math.cos(radians),
    y: 50 + radius * Math.sin(radians),
  };
}

function getMacroSplitFrameStyle(
  nutritionPalette: NutritionPalette,
  {
    compact,
    height,
    rounded,
  }: {
    compact?: boolean;
    height?: number;
    rounded?: boolean;
  }
): CSSProperties {
  return {
    width: "100%",
    minWidth: 0,
    minHeight: compact ? height : 52,
    height: compact ? height : undefined,
    borderRadius: compact ? (rounded ? Math.floor((height ?? 20) / 2) : 2) : 10,
    overflow: "hidden",
    border: `1px solid ${nutritionPalette.border}`,
    background: nutritionPalette.cardBgMuted,
    display: "flex",
    alignItems: "stretch",
    justifyContent: "center",
  };
}

function buildMacroSplit(
  nutritionPalette: NutritionPalette,
  netCarbs?: number | null,
  proteins?: number | null,
  fat?: number | null
): MacroSplitSegment[] {
  return [
    {
      key: "netCarbs",
      label: "Net Carbs",
      value: clampNonNegative(netCarbs),
      color: nutritionPalette.amberSoft,
      border: nutritionPalette.amber,
    },
    {
      key: "proteins",
      label: "Proteins",
      value: clampNonNegative(proteins),
      color: nutritionPalette.surfaceAccent,
      border: nutritionPalette.accent,
    },
    {
      key: "fat",
      label: "Fats",
      value: clampNonNegative(fat),
      color: nutritionPalette.orangeSoft,
      border: nutritionPalette.orange,
    },
  ];
}

function clampNonNegative(value?: number | null): number {
  return Math.max(0, parseMacroNumber(value) ?? 0);
}

export function formatMacroValue(value?: number | null): string {
  const parsedValue = parseMacroNumber(value);
  if (parsedValue === null) return "-";
  const numeric = Math.max(0, parsedValue);
  if (numeric < 10) return numeric.toFixed(1);
  if (numeric < 100) return numeric.toFixed(0);
  return numeric.toFixed(0);
}

export function formatMacroDisplayValue(key: MacroTagKey, value?: number | null): string {
  const numeric = formatMacroValue(value);
  if (numeric === "-") return numeric;
  return `${numeric}${MACRO_TAG_CONFIG[key].unit === "kcal" ? "kcal" : "g"}`;
}

function formatScoreValue(value?: number | null): string {
  const parsedValue = parseMacroNumber(value);
  if (parsedValue === null) return "-";
  const numeric = Math.max(0, parsedValue);
  if (numeric >= 10 || Number.isInteger(numeric)) return numeric.toFixed(0);
  return numeric.toFixed(1);
}

function parseMacroNumber(value?: number | null): number | null {
  if (value === null || value === undefined) return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function getStaticNutritionPalette(): NutritionPalette {
  return {
    cardBgMuted: "var(--app-card-bg-muted, #F8F7F1)",
    surfaceAccent: "var(--app-surface-accent, #F0F4ED)",
    text: "var(--app-text-color, #1F211C)",
    textMuted: "var(--app-text-muted, #62685D)",
    border: "var(--app-border-color, #E0DED4)",
    borderStrong: "var(--app-border-strong, #D4D0C4)",
    accent: "var(--app-accent, #4F7B58)",
    accentStrong: "var(--app-accent-strong, #31583A)",
    amber: "var(--app-amber, #D99A31)",
    amberSoft: "var(--app-amber-soft, #F3DFC0)",
    orange: "var(--app-orange, #D7742F)",
    orangeSoft: "var(--app-orange-soft, #EBCBB5)",
  };
}

function getNutritionPalette(token: ReturnType<typeof theme.useToken>["token"]): NutritionPalette {
  return {
    ...getStaticNutritionPalette(),
    cardBgMuted: token.colorFillSecondary,
    surfaceAccent: token.colorFillQuaternary,
    text: token.colorTextBase,
    textMuted: token.colorTextSecondary,
    border: token.colorBorder,
    borderStrong: token.colorBorderSecondary ?? token.colorBorder,
    accent: token.colorPrimary,
    accentStrong: token.colorPrimaryActive ?? token.colorPrimary,
  };
}

export default MacrosTable;
